import { useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  UserPlus,
  User as UserIcon,
  Mail,
  Calendar,
  Loader2,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Badge } from "../ui/Badge";
import type { User } from "../../types/database";

const roleLabels: Record<string, string> = {
  "Operator": "Operatore",
  "operator": "Operatore",
  "LR": "Rappresentante Legale",
  "admin": "Amministratore",
  "collaborator": "Collaboratore",
  "owner": "Proprietario",
  "viewer": "Visualizzatore",
};

const getRoleLabel = (role: string | null | undefined): string => {
  if (!role) return "";
  return roleLabels[role] || role;
};

interface CreatePackageWithCollaboratorModalProps {
  collaborator: User;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    collaboratorId: string;
  }) => Promise<void>;
  isCreating: boolean;
}

export function CreatePackageWithCollaboratorModal({
  collaborator,
  onClose,
  onCreate,
  isCreating,
}: CreatePackageWithCollaboratorModalProps) {
  const [packageData, setPackageData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
  });

  const handleSubmit = async () => {
    if (!packageData.name.trim()) return;

    await onCreate({
      name: packageData.name,
      description: packageData.description || undefined,
      start_date: packageData.start_date || undefined,
      end_date: packageData.end_date || undefined,
      collaboratorId: collaborator.id,
    });
  };

  const collaboratorName = [collaborator.first_name, collaborator.last_name]
    .filter(Boolean)
    .join(" ") || "Utente";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5 text-[#284899]" />
            Crea Itinerario con Collaboratore
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Crea un nuovo itinerario e invita automaticamente il collaboratore
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Collaborator Preview */}
          <div className="p-4 rounded-xl bg-[#284899]/5 border border-[#284899]/20">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="h-4 w-4 text-[#284899]" />
              <span className="text-sm font-medium text-[#284899]">
                Collaboratore selezionato
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#284899]/20 flex items-center justify-center">
                <UserIcon className="h-5 w-5 text-[#284899]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{collaboratorName}</p>
                {collaborator.email && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {collaborator.email}
                  </p>
                )}
              </div>
              {collaborator.role && (
                <Badge variant="secondary">
                  {getRoleLabel(collaborator.role)}
                </Badge>
              )}
            </div>
          </div>

          {/* Package Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nome Itinerario *</label>
              <Input
                placeholder="Es: Weekend in Toscana"
                value={packageData.name}
                onChange={(e) =>
                  setPackageData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione</label>
              <textarea
                placeholder="Breve descrizione dell'itinerario..."
                value={packageData.description}
                onChange={(e) =>
                  setPackageData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full h-20 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  Data Inizio
                </label>
                <Input
                  type="date"
                  value={packageData.start_date}
                  onChange={(e) =>
                    setPackageData((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  Data Fine
                </label>
                <Input
                  type="date"
                  value={packageData.end_date}
                  onChange={(e) =>
                    setPackageData((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Info */}
          <p className="text-sm text-muted-foreground bg-secondary/50 p-3 rounded-lg">
            L'itinerario sarà creato in stato bozza. {collaboratorName} ricevera
            una notifica e potrà accettare o rifiutare l'invito a collaborare.
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            disabled={!packageData.name.trim() || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Package className="h-4 w-4 mr-2" />
            )}
            Crea e Invita
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
